import { type IGlobalTab, TabType } from '@carboncopyco/types/desktop';
import { useEffect } from 'react';
import {
  MemoryRouter,
  Outlet,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import InnerTabsContainer from '../components/InnerTabs/InnerTabsContainer';
import { ResponsiveModeContextProvider } from '../store/context/ResponsiveModeContext';
import { TourCaptureRoute } from './TourCaptureRoute';
import TourEditorRoute from './TourEditorRoute';
import TourPreviewRoute from './TourPreviewRoute';

interface TabProps {
  tab: IGlobalTab;
}

const NavigationHandler = ({ tab }: { tab: IGlobalTab }) => {
  const navigate = useNavigate();

  const selectedFrameIndex = tab?.payload?.source?.selectedFrameIndex;

  useEffect(() => {
    if (selectedFrameIndex) {
      navigate(`?selectedFrameIndex=${selectedFrameIndex}`);
    }
  }, [selectedFrameIndex]);
  return null;
};

const TourTabWrapper = ({ tab }: TabProps) => {
  return (
    <div className="flex flex-col flex-1">
      <InnerTabsContainer globalTab={tab} />
      <Outlet />
    </div>
  );
};

export const TourTab = ({ tab }: TabProps) => {
  const { organizationId, workspaceId, payload, demoId, folderId } = tab;
  let initialRoute;
  const screenResolution = tab?.payload?.screenResolution;
  const isFolderIdParam = folderId ? `/${folderId}` : '';

  switch (tab.type) {
    case TabType.TOURS_CREATE:
      initialRoute = `/tour/capture/demo/${organizationId}/${workspaceId}/${demoId}${isFolderIdParam}`;
      if (payload.tourData?.originalTourId && payload.tourData?.insertIndex) {
        initialRoute += `?originalTourId=${payload.tourData.originalTourId}&insertIndex=${payload.tourData.insertIndex}`;
      }
      if (payload.tourData?.tourEditorTabId) {
        initialRoute += `&tourEditorTabId=${payload.tourData?.tourEditorTabId}`;
      }
      if (screenResolution) {
        initialRoute += `&width=${screenResolution.width}&height=${screenResolution.height}&type=${screenResolution.type}`;
      }
      break;
    case TabType.LIVE_TOURS_CREATE: {
      const {
        tourId,
        appUrl,
        playerToken,
        fileServiceToken,
        originalTourId,
        insertIndex,
        tourEditorTabId,
      } = tab?.payload.source;
      initialRoute = `/tour/capture/url/${organizationId}/${workspaceId}${isFolderIdParam}?tourId=${tourId}&appUrl=${appUrl}&playerToken=${playerToken}&fileServiceToken=${fileServiceToken}`;
      if (
        originalTourId &&
        originalTourId !== 'undefined' &&
        insertIndex &&
        insertIndex !== 'undefined'
      ) {
        initialRoute += `&originalTourId=${originalTourId}&insertIndex=${insertIndex}`;
      }
      if (tourEditorTabId) {
        initialRoute += `&tourEditorTabId=${tourEditorTabId}`;
      }
      if (screenResolution) {
        initialRoute += `&width=${screenResolution.width}&height=${screenResolution.height}&type=${screenResolution.type}`;
      }
      break;
    }
    case TabType.TOURS_PREVIEW: {
      const tour = tab?.payload.tour;
      initialRoute = `/tour/preview/${organizationId}/${workspaceId}/${tour.id}`;
      break;
    }
    case TabType.GROUP_TOUR_PRESENTATION: {
      const tour = tab?.payload.tour;
      initialRoute = `/tour/presentation/${organizationId}/${workspaceId}/${tour.id}`;
      break;
    }
    case TabType.GROUP_TOUR_PREVIEW: {
      const tour = tab?.payload.tour;
      initialRoute = `/tour/preview/${organizationId}/${workspaceId}/${tour.id}`;
      break;
    }
    case TabType.TOURS_EDITOR: {
      const tour = tab?.payload.tour;
      initialRoute = `/tour/editor/${organizationId}/${workspaceId}/${tour.id}`;
      break;
    }
    default:
      initialRoute = 'PAGE NOT FOUND';
  }

  return (
    <MemoryRouter initialEntries={[initialRoute]}>
      <Routes>
        <Route
          path="/tour/capture/*"
          element={<TourTabWrapper tab={tab} />}
        >
          <Route
            path="demo/:organizationId/:workspaceId/:demoId/:folderId?"
            element={
              <ResponsiveModeContextProvider>
                <TourCaptureRoute tab={tab} />
              </ResponsiveModeContextProvider>
            }
          />
          <Route
            path="url/:organizationId/:workspaceId/:folderId?"
            element={
              <ResponsiveModeContextProvider>
                <TourCaptureRoute tab={tab} />
              </ResponsiveModeContextProvider>
            }
          />
        </Route>
        <Route
          path="/tour/*"
          element={
            <div className="flex flex-col flex-1">
              <NavigationHandler tab={tab} />
              <Outlet />
            </div>
          }
        >
          <Route
            path="preview/:organizationId/:workspaceId/:tourId"
            element={<TourPreviewRoute tab={tab} />}
          />
          <Route
            path="presentation/:organizationId/:workspaceId/:tourId"
            element={
              <TourPreviewRoute
                tab={tab}
                isPresentation
              />
            }
          />
          <Route
            path="editor/:organizationId/:workspaceId/:tourId"
            element={<TourEditorRoute tabId={tab.id} />}
          />
        </Route>
        <Route
          path="*"
          element={<>PAGE NOT FOUND - {initialRoute}</>}
        />
      </Routes>
    </MemoryRouter>
  );
};
